import { IonCard, IonCol, IonRow } from '@ionic/react';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { FilterReduxCommandCreator } from 'appRedux/actions/filterCommandCreator';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import { ICasinoSearchData, IState } from 'appRedux/createStore';
import clsx from 'clsx';
import { FilterTypeEnum, getCleanGameName, OrderByEnum, TimeFrameValueEnum } from 'common/common';
import Search from 'components/Search';
import { InsightValueEnum } from 'config/Insights';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Subscription } from 'rxjs';
import { CasinoService } from 'services/casino';
import { isInsightCardRow } from 'services/insightCasino';
import { setActiveMachinesCatalog } from 'lib/machineCatalog';

interface ISearchModalProps {
  searchBorder?: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  closeHandler: () => void;
  onClickIndividualItem: (slotNumber: number) => void;
}

export const SearchModal = ({
  searchBorder,
  title,
  closeHandler,
  onClickIndividualItem,
}: ISearchModalProps) => {
  const [searchLoading, setSearchLoading] = useState(false);
  const [clearSearchValue, setClearSearchValue] = useState(false);
  const dispatch = useDispatch();
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const slotCommands = SlotReduxCommandCreator(dispatch);
  const filterCommands = FilterReduxCommandCreator(dispatch);

  const casinoSvc = new CasinoService();
  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;

  const casinoSearchData = useSelector<IState>(
    (state) => state.app.persistedState.casinoSearchData
  ) as ICasinoSearchData;

  const onItemClick = (slotNumbers: number[]): void => {
    slotCommands.ClearSelectedSlots();
    filterCommands.SetFilterOptions(FilterTypeEnum.hottestSlots, {
      insight: InsightValueEnum.return,
      timeframe: TimeFrameValueEnum.months1,
      orderby: OrderByEnum.Desc,
    });
    onClickIndividualItem(slotNumbers[0]);
    setClearSearchValue(true);
    closeHandler();
  };

  const fetchSearchData = useCallback(() => {
    setSearchLoading(true);
    let subscription: Subscription | null = null;
    if (casinoId && casinoId > 0) {
      if (!casinoSearchData || casinoSearchData.casinoId != casinoId) {
        subscription = casinoSvc.GetSearchData(casinoId).subscribe({
          next(casinoResponse) {
            if (casinoResponse) {
              // Insight rows: keep API game_name / title as-is (no Title/Theme split).
              const cleanedDataSet = casinoResponse.DataSet.map((item) => {
                const keepRaw =
                  isInsightCardRow(item as unknown as Record<string, unknown>) ||
                  (item as { source?: unknown }).source === 'insight';
                return keepRaw ? item : { ...item, game_name: getCleanGameName(item.game_name) };
              });
              casinoCommands.SetCasinoSearchData({ casinoId, data: cleanedDataSet });
            }
            setSearchLoading(false);
          },
          error(_err) {
            setSearchLoading(false);
          },
          complete() {
            setSearchLoading(false);
          },
        });
      } else {
        // Re-hydrate module catalog from Redux (same desync fix as HotListReport).
        setActiveMachinesCatalog(casinoSearchData.data ?? null);
        setSearchLoading(false);
      }
    } else {
      setSearchLoading(false);
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [casinoId]);

  useEffect(() => {
    let isIgnore = false;
    if (!isIgnore) {
      fetchSearchData();
    }
    return () => {
      isIgnore = true;
    };
  }, []);

  return (
    <IonCard id="tour-modal" className="modal-container app-tour-tooltip search-modal">
      <h5>{title}</h5>
      <IonRow className="tour-modal-buttons">
        <IonCol>
          <Search
            customCls={clsx(
              'machine-nav-search-input',
              searchBorder ? 'search-nav-border' : '',
              'active'
            )}
            searchData={casinoSearchData?.data}
            searchLoading={searchLoading}
            shouldFocus
            onClickItem={onItemClick}
            closeHandler={closeHandler}
            cssClass={'search-container__left'}
          />
        </IonCol>
      </IonRow>
    </IonCard>
  );
};
